/* Saudization (Nitaqat), government fees, validators, billing math.
 * Mirrors api/internal/domain/compliance.go. */
import type { Band, Employee, EmployeeInput, FeeResult, NitaqatResult, Subscription } from "../data/types";
import { addDays, daysTo, isISODate } from "./dates";
import { round2 } from "./payroll";
import { ibanMessage, idMessage, validIbanFor, validIdFor } from "./country";

export const BANDS: Band[] = [
  { key: "Red", to: 8, tone: "bad" },
  { key: "Low Green", to: 17, tone: "ok" },
  { key: "Medium Green", to: 24, tone: "ok" },
  { key: "High Green", to: 32, tone: "ok" },
  { key: "Platinum", to: 100, tone: "gold" },
];

export function bandOf(pct: number, bands: Band[] = BANDS): Band {
  for (const b of bands) if (pct < b.to) return b;
  return bands[bands.length - 1];
}

/** Nitaqat weighting: only Qiwa-authenticated Saudis count; basic < 4000 → 0.5; disabled → 4.
 *  extraSaudi / extraNonSaudi simulate planned hires (each extra Saudi counts 1). */
export function nitaqat(emps: Employee[], extraSaudi = 0, extraNonSaudi = 0): NitaqatResult {
  const act = emps.filter((e) => e.status !== "Terminated");
  let w = 0, raw = 0, notDoc = 0, under = 0;
  for (const e of act) {
    if (e.nationality !== "SA") continue;
    raw++;
    if (e.qiwa.status !== "Authenticated") { notDoc++; continue; }
    const x = e.disabled ? 4 : e.basic < 4000 ? 0.5 : 1;
    if (e.basic < 4000) under++;
    w += x;
  }
  const sa = Math.max(0, Math.floor(extraSaudi || 0)), ns = Math.max(0, Math.floor(extraNonSaudi || 0));
  w += sa;
  const total = act.length + sa + ns;
  const docd = act.filter((e) => e.qiwa.status === "Authenticated").length;
  const pct = total > 0 ? (w / total) * 100 : 0;
  return {
    weighted: w, raw, total, pct: Math.round(pct * 100) / 100, notDoc, under,
    docPct: act.length ? Math.round((docd / act.length) * 10000) / 100 : 0, docd, n: act.length, band: bandOf(pct).key,
  };
}

/** How many extra Saudi hires are needed to reach `targetPct` (∞-safe; returns 0 if already there). */
export function saudisNeeded(emps: Employee[], targetPct: number): number {
  for (let k = 0; k <= 1000; k++) if (nitaqat(emps, k, 0).pct >= targetPct) return k;
  return -1;
}

export const GOV_TYPES = ["Iqama renewal", "Exit re-entry (single)", "Exit re-entry (multiple)", "Final exit", "Absence report (Huroob)"] as const;

export function feeCalc(type: string, months: number): FeeResult {
  const m = Math.max(0, months || 0);
  if (type === "Iqama renewal") {
    const iq = (650 * m) / 12, levy = 800 * m, wp = (100 * m) / 12;
    return { iq: round2(iq), levy: round2(levy), wp: round2(wp), total: round2(iq + levy + wp) };
  }
  if (type === "Exit re-entry (single)") return { total: 200 + Math.max(0, m - 2) * 100 };
  if (type === "Exit re-entry (multiple)") return { total: 500 + Math.max(0, m - 3) * 200 };
  return { total: 0 };
}

/** Absence report is blocked when the Iqama has fewer than 60 days left. */
export function absenceReportBlocked(e: Employee, today: string): boolean {
  return !!e.iqamaExpiry && daysTo(today, e.iqamaExpiry) < 60;
}

export const normIban = (s: string) => String(s || "").replace(/\s+/g, "").toUpperCase();
export function validIban(s: string): boolean { return /^SA\d{22}$/.test(normIban(s)); }
export function validNationalId(id: string, nationality: string): boolean {
  const s = String(id || "").trim();
  return /^\d{10}$/.test(s) && s[0] === (nationality === "SA" ? "1" : "2");
}
export function validEmail(s: string): boolean { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "")); }

/** Employee validation; `country` = the tenant's country (ID and IBAN formats). */
export function validateEmployee(f: Partial<EmployeeInput>, country = "SA"): Record<string, string> {
  const err: Record<string, string> = {};
  if (!f.nameEn || !f.nameEn.trim()) err.nameEn = "required";
  if (!f.nameAr || !f.nameAr.trim()) err.nameAr = "required";
  if (!f.nationality) err.nationality = "required";
  if (!validIdFor(f.idNumber || "", f.nationality || "", country)) err.idNumber = idMessage(f.nationality || "", country);
  if (!validIbanFor(f.iban || "", country)) err.iban = ibanMessage(country);
  if (f.email && !validEmail(f.email)) err.email = "invalid email";
  if (typeof f.basic !== "number" || !isFinite(f.basic) || f.basic <= 0) err.basic = "must be > 0";
  for (const k of ["housing", "transport", "other"] as const) {
    const v = f[k];
    if (v != null && (typeof v !== "number" || !isFinite(v) || v < 0)) err[k] = "must be ≥ 0";
  }
  if (!f.joined || !isISODate(f.joined)) err.joined = "valid date required";
  if (f.dob && !isISODate(f.dob)) err.dob = "invalid date";
  if (!f.dept) err.dept = "required";
  if (!f.branch) err.branch = "required";
  if (!f.title || !f.title.trim()) err.title = "required";
  return err;
}

/** Default allowances from basic using settings percentages. */
export function allowancesFor(basic: number, pct: { housing: number; transport: number }) {
  return { housing: Math.round((basic * pct.housing) / 100), transport: Math.round((basic * pct.transport) / 100) };
}

/* ---------- Billing ---------- */
export const VAT_RATE = 0.15;
export const AI_PACK_PRICE = 99;
export function subAmount(prices: Record<string, number>, plan: string, cycle: "m" | "y", seats: number, aiPack = 0) {
  const p = prices[plan];
  if (p == null) throw new Error("unknown plan: " + plan);
  const s = Math.max(1, Math.floor(seats || 0));
  const m = p * s;
  let amt = cycle === "y" ? Math.round(m * 12 * 0.8) : m;
  amt += (aiPack || 0) * (cycle === "y" ? 12 : 1) * AI_PACK_PRICE;
  const vat = round2(amt * VAT_RATE);
  return { amount: amt, vat, total: round2(amt + vat) };
}
export function trialEnd(sub: Subscription): string { return addDays(sub.trialStart, sub.trialDays); }
export function trialLeft(sub: Subscription, today: string): number { return Math.max(0, daysTo(today, trialEnd(sub))); }
export function paymentRef(cr: string, n: number | string) { return "SHR-" + cr + "-" + n; }
/** Period end after verification: from max(today, trialEnd) + 1 month / 1 year.
 *  The day is clamped to the target month's last day (2026-01-31 + 1m → 2026-02-28; 2028-02-29 + 1y → 2029-02-28). */
export function activeUntilAfter(start: string, cycle: "m" | "y"): string {
  const [y, m, d] = start.split("-").map(Number);
  let ty = y, tm = m; // tm is 1-based
  if (cycle === "y") ty = y + 1;
  else if (m === 12) { ty = y + 1; tm = 1; } else tm = m + 1;
  const last = new Date(Date.UTC(ty, tm, 0)).getUTCDate();
  return `${String(ty).padStart(4, "0")}-${String(tm).padStart(2, "0")}-${String(Math.min(d, last)).padStart(2, "0")}`;
}
